import { create, StateCreator } from "zustand";
import { AccountAssets, AdCreative, AdsetAsset, CampaignAsset, Folder } from "./types";
import { ISidebarItem } from "../../components/common/sidebar/SidebarItem";

export interface CreativeLibraryGlobalSlice {
  loadingAccountAssets: boolean;
  accountAssets: AccountAssets | null;
  setLoadingAccountAssets: (loadingAccountAssets: boolean) => void;
  setAccountAssets: (accountAssets: AccountAssets) => void;
  lastUsedAsset: {
    campaign: CampaignAsset;
    adset: AdsetAsset;
  } | null;
  setLastUsedAsset: (
    lastUsedAsset: {
      campaign: CampaignAsset;
      adset: AdsetAsset;
    } | null,
  ) => void;
}
export interface SidebarSlice {
  folders: Folder[];
  setFolders: (folders: Folder[]) => void;
  selectedSidebarItem: ISidebarItem | null;
  setSelectedSidebarItem: (item: ISidebarItem | null) => void;
}

export interface ExploreSlice {
  loadingExploreCreatives: boolean;
  setLoadingExploreCreatives: (loadingCreatives: boolean) => void;
  exploreAdCreatives: AdCreative[];
  setExploreAdCreatives: (adsCreatives: AdCreative[]) => void;
}

export interface FolderSlice {
  loadingFolderCreatives: boolean;
  setLoadingFolderCreatives: (loadingCreatives: boolean) => void;
  folderAdCreatives: AdCreative[];
  setFolderAdCreatives: (adsCreatives: AdCreative[]) => void;
}

export interface PublishSlice {
  // gallery states for unpublished table
  openGallery: boolean; // whether the gallery is open
  setOpenGallery: (openGallery: boolean) => void;
  galleryAdCreatives: AdCreative[];
  setGalleryAdCreatives: (rowAdCreatives: AdCreative[]) => void;
}

type CreativeLibraryStore = SidebarSlice &
  ExploreSlice &
  FolderSlice &
  PublishSlice &
  CreativeLibraryGlobalSlice;

const createSidebarSlice: StateCreator<CreativeLibraryStore, [], [], SidebarSlice> = (
  set,
) => ({
  folders: [],
  setFolders: (folders) => set({ folders }),
  selectedSidebarItem: null,
  setSelectedSidebarItem: (item) => set({ selectedSidebarItem: item }),
});

const createExploreSlice: StateCreator<CreativeLibraryStore, [], [], ExploreSlice> = (
  set,
) => ({
  loadingExploreCreatives: false,
  setLoadingExploreCreatives: (loadingExploreCreatives) =>
    set({ loadingExploreCreatives }),
  exploreAdCreatives: [],
  setExploreAdCreatives: (exploreAdCreatives) => set({ exploreAdCreatives }),
});

const createFolderSlice: StateCreator<CreativeLibraryStore, [], [], FolderSlice> = (
  set,
) => ({
  loadingFolderCreatives: false,
  setLoadingFolderCreatives: (loadingFolderCreatives) => set({ loadingFolderCreatives }),
  folderAdCreatives: [],
  setFolderAdCreatives: (folderAdCreatives) => set({ folderAdCreatives }),
});
const createPublishSlice: StateCreator<CreativeLibraryStore, [], [], PublishSlice> = (
  set,
) => ({
  openGallery: false,
  setOpenGallery: (openGallery) => set({ openGallery }),
  galleryAdCreatives: [],
  setGalleryAdCreatives: (galleryAdCreatives) => set({ galleryAdCreatives }),
});

const createCreativeLibraryGlobalSlice: StateCreator<
  CreativeLibraryStore,
  [],
  [],
  CreativeLibraryGlobalSlice
> = (set) => ({
  loadingAccountAssets: false,
  accountAssets: null,
  setLoadingAccountAssets: (loadingAccountAssets) => set({ loadingAccountAssets }),
  setAccountAssets: (accountAssets) => set({ accountAssets }),
  lastUsedAsset: null,
  setLastUsedAsset: (lastUsedAsset) => set({ lastUsedAsset }),
});

export const useCreativeLibraryStore = create<CreativeLibraryStore>()((...a) => ({
  ...createSidebarSlice(...a),
  ...createExploreSlice(...a),
  ...createFolderSlice(...a),
  ...createCreativeLibraryGlobalSlice(...a),
  ...createPublishSlice(...a),
}));
